import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import usePostUpdateWebhook from '../../../../hooks/useApi/usePostUpdateWebhook';
import useFetchWebhookInfo from '../../../../hooks/useApi/useFetchWebhookInfo';
import UpdateWebhooksForm from './UpdateWebhooksForm';
import UpdateWebhooksReview from './UpdateWebhooksReview';
import useBuildUpdateWebhookRows from './buildUpdateWebhooksRows.helper';
import UpdateWebhookContext, { UpdateWebhookCtx, WebhookUpdateError } from './Dialogue/UpdateWebhookContext';
import UpdateWebhookDialogue from './Dialogue/UpdateWebhookDialogue';
import { UpdateWebhookDialogueHandler, DialogueHandlerProps } from './Dialogue/types';
import { useClientData } from '../../../../providers/ClientData.provider';
import useDataFetchIndicator from '../../../../hooks/useDataFetchIndicator';
import { DefaultValues, ToggleValues } from './types';

type FC = FunctionComponent;

const UpdateWebhookContainer: FC = () => {
  const reviewRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const [updateErrors, setUpdateErrors] = useState<WebhookUpdateError[]>([]);
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [showUpdateErrorAlert, setShowUpdateErrorAlert] = useState(false);
  const [showUpdateSuccessAlert, setShowUpdateSuccessAlert] = useState(false);
  const [, , updateWebhook] = usePostUpdateWebhook({});
  const [webhookDetails, getWebhookDetails] = useFetchWebhookInfo({});
  const webhookRows = useBuildUpdateWebhookRows();
  const labelByEvent: Record<string, string> = Object.fromEntries(
    webhookRows.map((row) => [String(row.id), String(row.label)]),
  );
  const [defaultValues, setDefaultValues] = useState<DefaultValues>({
    transaction: '',
    elinx: '',
    bankaccount: '',
    batchrequest: '',
    accountonboarding: '',
    batchdetail: '',
    scheduledtransactions: '',
    accountverifications: '',
    transactiongroup: '',
    accountbalance: '',
    clientaccountbalance: '',
    paymentreceived: '',
    accountlimit: '',
    virtualtransaction: '',
    creditcard: '',
    debitcard: '',
  });
  const [toggleValues, setToggleValues] = useState<ToggleValues>({
    transaction: false,
    elinx: false,
    bankaccount: false,
    batchrequest: false,
    accountonboarding: false,
    batchdetail: false,
    scheduledtransactions: false,
    accountverifications: false,
    transactiongroup: false,
    accountbalance: false,
    clientaccountbalance: false,
    paymentreceived: false,
    accountlimit: false,
    virtualtransaction: false,
    creditcard: false,
    debitcard: false,
  });
  const [review, setReview] = useState(false);
  const { response } = useClientData();

  const [webHookRequests, setWebhookRequests] = useState<vp.UpdateWebhookRequest[]>([]);
  useEffect(() => {
    getWebhookDetails({
      PortalUser: response.LoginUserName,
    });
  }, []);

  useEffect(() => {
    Object.values(webhookDetails?.Webhooks || {}).forEach((obj) => {
      setDefaultValues((prevValues) => ({
        ...prevValues,
        [obj.Event]: obj.Url,
      }));
      setToggleValues((prevValues) => ({
        ...prevValues,
        [obj.Event]: !!obj.Url,
      }));
    });

    setWebhookRequests(
      Object.values(vp.webhookEventList).map((event) => ({
        WebHookUrl: webhookDetails?.Webhooks ? webhookDetails?.Webhooks[event]?.Url : '',
        Disabled: false,
        PortalUser: response.LoginUserName,
        Type: event,
      })),
    );
  }, [webhookDetails]);

  const UpdateWebhook: UpdateWebhookCtx = {
    showUpdateSuccessAlert,
    showUpdateErrorAlert,
    updateErrors,
  };
  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<UpdateWebhookDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: UpdateWebhookDialogueHandler = {
        update: {
          success: {
            default: () => {
              setShowUpdateSuccessAlert(false);
              history.push('/settings/webhooks');
            },
          },
          error: {
            default: () => {
              setShowUpdateErrorAlert(false);
              setUpdateErrors([]);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
    onReview: (values: DefaultValues) => {
      setReview(true);
      setDefaultValues(values);
      setWebhookRequests(
        Object.values(vp.webhookEventList).map((event) => ({
          WebHookUrl: toggleValues[event] ? values[event] : '',
          Disabled: !toggleValues[event],
          PortalUser: response.LoginUserName,
          Type: event,
        })),
      );
    },
    onUpdate: async () => {
      try {
        setDataFetchingInfo(true, 'Loading...', 'fullScreen');
        const results = await Promise.all(
          webHookRequests.map(async (req) => {
            try {
              const res = await updateWebhook(req);
              return { type: req.Type, success: res.Success, message: res.ErrorMessage || '' };
            } catch (error) {
              return { type: req.Type, success: false, message: error instanceof Error ? error.message : '' };
            }
          }),
        );
        const failures = results.filter((result) => !result.success);

        if (failures.length === 0) {
          setShowUpdateSuccessAlert(true);
        } else {
          setUpdateErrors(
            failures.map((failure) => ({
              label: labelByEvent[String(failure.type)] || String(failure.type),
              message: failure.message,
            })),
          );
          setShowUpdateErrorAlert(true);
        }
      } catch (error) {
        console.error('Error making webhook requests: ', error);
      } finally {
        setDataFetchingInfo(false);
      }
    },
    onBack: () => {
      setReview(false);
    },
  };

  useEffect(() => {
    if (review) {
      if (reviewRef !== null && reviewRef.current !== null) {
        reviewRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, [review]);

  return (
    <div ref={reviewRef}>
      <UpdateWebhookContext.Provider value={UpdateWebhook}>
        {!review && (
          <UpdateWebhooksForm
            defaultValues={defaultValues}
            toggleValues={toggleValues}
            setToggleValues={setToggleValues}
            onSubmit={handlers.onReview}
          />
        )}
        {review && (
          <UpdateWebhooksReview
            defaultValues={defaultValues}
            toggleValues={toggleValues}
            onSubmit={handlers.onUpdate}
            onBack={handlers.onBack}
          />
        )}
        <UpdateWebhookDialogue handleDialogue={handlers.handleDialogue} />
      </UpdateWebhookContext.Provider>
    </div>
  );
};

export default UpdateWebhookContainer;
